import { AnyID, HiddenWall, Item, Setter, Walls } from '@types'
import { quatYToDeg } from '@utils/conversion/quatYToDeg'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import { radians } from '@utils/generalMaths/geometry'
import isHiddenWall from '@utils/sceneObjects/assertions/isHiddenWall'
import getId from '@utils/sceneObjects/getId'
import { getBaseHiddenWall } from '@utils/sceneObjects/hiddenWalls/getBaseHiddenWall'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { Euler, Matrix4, Quaternion } from 'three'

export const createHiddenWallsActions = (set: Setter) => ({
  drawHiddenWall: (heights?: number) => {
    set((store) => {
      const height = !heights ? store.prefs.ALTOPARED : heights
      const width = 3000
      const thickness = 100
      const hiddenWallID = getId('hiddenWall')
      const position = toVector3([0, height / 2, 0])

      const newMatrix = new Matrix4()
      newMatrix.makeRotationY(radians(-90)).setPosition(position)

      store.items.set(
        hiddenWallID,
        getBaseHiddenWall(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: 0,
            z: 0,
            id: hiddenWallID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(hiddenWallID)
      store.cameraCenter = [0, 4000, 0]
      store.currentObjectID = hiddenWallID
      store.select(hiddenWallID, false)
    })
  },
  editHiddenWallProperty: <T extends HiddenWall>(
    id: string,
    config?: Partial<Record<keyof T, T[keyof T]>>,
  ) => {
    set((store) => {
      const item = store.items.get(id as AnyID)
      if (!isHiddenWall(item)) return
      if (!config) return
      for (const [property, value] of Object.entries(config)) {
        if (property === 'id') continue
        if (property === 'type')
          continue
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(item as any)[property] = value
      }
    })
  },
  editHiddenWallAngle: (id: string, angle: number) => {
    set((store) => {
      const hiddenWall = store.items.get(id as AnyID) as HiddenWall
      const position = toVector3()

      if (!isHiddenWall(hiddenWall)) return
      hiddenWall.matrix.decompose(position, new Quaternion(), toVector3())
      hiddenWall.matrix.makeRotationY(radians(-angle))
      hiddenWall.matrix.setPosition(position)
    })
  },
  editHiddenWallAngleX: (id: string, angle: number) => {
    set((store) => {
      const hiddenWall = store.items.get(id as AnyID) as HiddenWall
      const position = toVector3()
      if (!isHiddenWall(hiddenWall)) return
      hiddenWall.matrix.decompose(position, new Quaternion(), toVector3())
      hiddenWall.matrix.makeRotationX(radians(-angle))
      hiddenWall.matrix.setPosition(position)
    })
  },
  editHiddenWallAngleZ: (id: string, angle: number) => {
    set((store) => {
      const hiddenWall = store.items.get(id as AnyID) as HiddenWall
      const position = toVector3()
      if (!isHiddenWall(hiddenWall)) return
      hiddenWall.matrix.decompose(position, new Quaternion(), toVector3())
      hiddenWall.matrix.makeRotationZ(radians(-angle))
      hiddenWall.matrix.setPosition(position)
    })
  },
  editHiddenWallHeightFromFloor: (id: string, height: number) => {
    set((store) => {
      const item = store.items.get(id as AnyID) as Item
      if (!item) return

      const pos = toVector3()
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, height + item.height / 2, pos.z)
      item.y = height
    })
  },
  incrementHiddenWallZ: (id: string, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id as AnyID) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementHiddenWallY: (id: string, y: number) => {
    const axis = 'y'
    set((store) => {
      const item = store.items.get(id as AnyID) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, y, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementHiddenWallX: (id: string, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id as AnyID) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  updateStartEndHiddenWall: (id: string) => {
    set((store) => {
      const hiddenWall = store.items.get(id as AnyID) as HiddenWall
      if (!hiddenWall) return
      const matrix = hiddenWall.matrix
      const { rotation: rot, position: pos } = decomposeMatrix(matrix)
      const quatY = radians(quatYToDeg(rot))
      const endX =
        pos.x + (hiddenWall.width * Math.cos(-quatY) - hiddenWall.thickness * Math.sin(-quatY)) / 2
      const endZ =
        pos.z + (hiddenWall.width * Math.sin(-quatY) + hiddenWall.thickness * Math.cos(-quatY)) / 2
      const startX =
        pos.x - (hiddenWall.width * Math.cos(-quatY) + hiddenWall.thickness * Math.sin(-quatY)) / 2
      const startZ =
        pos.z - (hiddenWall.width * Math.sin(-quatY) - hiddenWall.thickness * Math.cos(-quatY)) / 2

      const endX1 =
        pos.x - (hiddenWall.width * Math.cos(-quatY) - hiddenWall.thickness * Math.sin(-quatY)) / 2
      const endZ1 =
        pos.z - (hiddenWall.width * Math.sin(-quatY) + hiddenWall.thickness * Math.cos(-quatY)) / 2
      const startX1 =
        pos.x + (hiddenWall.width * Math.cos(-quatY) + hiddenWall.thickness * Math.sin(-quatY)) / 2
      const startZ1 =
        pos.z + (hiddenWall.width * Math.sin(-quatY) - hiddenWall.thickness * Math.cos(-quatY)) / 2

      hiddenWall.start = [startX, startZ]
      hiddenWall.end = [endX, endZ]
      hiddenWall.start1 = [startX1, startZ1]
      hiddenWall.end1 = [endX1, endZ1]
    })
  },
  drawHiddenWallFromWall: (id: string) => {
    set((store) => {
      const wall = store.items.get(id as AnyID) as Walls | HiddenWall
      const height = wall.height
      const width = 1000
      const thickness = 100
      const hiddenWallID = getId('hiddenWall')
      const position = toVector3()

      const rotation = wall.rotation
      const x =
        wall.x -
        ((width / 2 + wall.thickness / 2) * Math.sin(rotation) +
          (wall.thickness / 2) * Math.cos(rotation))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rotation) -
          (wall.thickness / 2) * Math.sin(rotation))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rotation), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        return angle - Math.PI / 2
      }
      store.items.set(
        hiddenWallID,
        getBaseHiddenWall(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rotation),
            z: z + (thickness / 2) * Math.cos(rotation),
            id: hiddenWallID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(hiddenWallID)
      store.currentObjectID = hiddenWallID
    })
  },
  drawHiddenWallFromHiddenWall: (id: string) => {
    set((store) => {
      const wall = store.items.get(id as AnyID) as HiddenWall
      const height = wall.height
      const width = 1000
      const thickness = 100
      const hiddenWallID = getId('hiddenWall')
      const position = toVector3()

      const rotation = decomposeMatrix(wall.matrix).rotation
      const rot = radians(quatYToDeg(rotation))
      const x =
        wall.x -
        ((width + wall.thickness / 2) * Math.sin(rot) + (wall.thickness / 2) * Math.cos(rot))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rot) - (wall.thickness / 2) * Math.sin(rot))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rot), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        return angle - Math.PI / 2
      }
      store.items.set(
        hiddenWallID,
        getBaseHiddenWall(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rot),
            z: z + (thickness / 2) * Math.cos(rot),
            id: hiddenWallID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(hiddenWallID)
      store.currentObjectID = hiddenWallID
      store.select(hiddenWallID, false)
    })
  },
  projectHiddenWallOnWall: (hiddenWallID: string, wallID: string, matrix: Matrix4) => {
    set((store) => {
      const wall = store.items.get(wallID as AnyID) as Walls
      const hiddenWall = store.items.get(hiddenWallID as AnyID) as HiddenWall
      const hiddenWallWidth = hiddenWall.width
      if (!wall) return
      const rotation = wall.rotation
      const hiddenWallPosition = toVector3()
      matrix.decompose(hiddenWallPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const hiddenWallToStart = hiddenWallPosition.clone().sub(start)
      const projectedVector = hiddenWallToStart.projectOnVector(wallDirection.clone().normalize())
      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(projectedVector.length()))
      const finalX = adjustedPosition.x - (hiddenWallWidth / 2) * Math.sin(rotation)
      const finalZ = adjustedPosition.z + (hiddenWallWidth / 2) * Math.cos(rotation)
      const finalY =
        hiddenWallPosition.y - hiddenWall.height / 2 < 0
          ? 0
          : hiddenWallPosition.y - hiddenWall.height / 2
      function getRightPerpendicular(angle: number) {
        return angle - Math.PI / 2
      }

      if (!hiddenWall) return
      if (!hiddenWall.visible) hiddenWall.visible = true

      hiddenWall.matrix.makeRotationFromEuler(
        new Euler(0, getRightPerpendicular(-rotation), 0, 'XYZ'),
      )
      hiddenWall.matrix.setPosition(finalX, hiddenWallPosition.y, finalZ)
      hiddenWall.x = finalX
      hiddenWall.y = finalY
      hiddenWall.z = finalZ
      hiddenWall.snappedWall = wallID
    })
  },
  projectItemOnHiddenWall: (itemID: AnyID, hiddenWallID: AnyID, itemMatrix: Matrix4) => {
    set((store) => {
      const hiddenWall = store.items.get(hiddenWallID) as HiddenWall
      const item = store.items.get(itemID) as Item
      const itemWidth = item.width
      if (!hiddenWall) return
      const { rotation } = decomposeMatrix(hiddenWall.matrix)
      const thickness = hiddenWall.thickness
      const quatY = radians(quatYToDeg(rotation))

      const itemPosition = toVector3()
      itemMatrix.decompose(itemPosition, toQuaternion(), toVector3())
      const end = toVector3(hiddenWall.end)
      const start = toVector3(hiddenWall.start)
      const hiddenWallDirection = end.clone().sub(start)

      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(hiddenWallDirection.clone().normalize())
      const adjustedPosition = start
        .clone()
        .add(hiddenWallDirection.normalize().multiplyScalar(projectedVector.length()))

      const finalX = adjustedPosition.x - (itemWidth / 2) * Math.sin(quatY)
      const finalZ = adjustedPosition.z + (itemWidth / 2) * Math.cos(quatY)
      const finalY = itemPosition.y - item.height / 2 < 0 ? 0 : itemPosition.y - item.height / 2

      function getRightPerpendicular(angle: number) {
        return angle - Math.PI / 2
      }

      if (!item) return
      if (!item.visible) item.visible = true

      item.matrix.makeRotationFromEuler(new Euler(0, getRightPerpendicular(-quatY), 0, 'XYZ'))

      const yPos = itemPosition.y

      const offsetX = (thickness / 2) * Math.cos(quatY)
      const offsetZ = (thickness / 2) * Math.sin(quatY)

      const finalXWithOffset = finalX + offsetX
      const finalZWithOffset = finalZ + offsetZ

      item.matrix.setPosition(finalXWithOffset, yPos, finalZWithOffset)
      item.x = finalXWithOffset
      item.y = finalY
      item.z = finalZWithOffset
      item.snappedWall = hiddenWallID
    })
  },
  projectHiddenWallOnHiddenWall: (itemID: AnyID, hiddenWallID: AnyID, itemMatrix: Matrix4) => {
    set((store) => {
      const hiddenWall = store.items.get(hiddenWallID) as HiddenWall
      const item = store.items.get(itemID) as HiddenWall
      const itemWidth = item.width
      if (!hiddenWall) return
      const { rotation } = decomposeMatrix(hiddenWall.matrix)
      const thickness = hiddenWall.thickness
      const quatY = radians(quatYToDeg(rotation))

      const itemPosition = toVector3()
      itemMatrix.decompose(itemPosition, toQuaternion(), toVector3())
      const end = toVector3(hiddenWall.end)
      const start = toVector3(hiddenWall.start)
      const hiddenWallDirection = end.clone().sub(start)

      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(hiddenWallDirection.clone().normalize())
      const adjustedPosition = start
        .clone()
        .add(hiddenWallDirection.normalize().multiplyScalar(projectedVector.length()))

      const finalX = adjustedPosition.x - (itemWidth / 2) * Math.sin(quatY)
      const finalZ = adjustedPosition.z + (itemWidth / 2) * Math.cos(quatY)
      const finalY = itemPosition.y - item.height / 2 < 0 ? 0 : itemPosition.y - item.height / 2

      function getRightPerpendicular(angle: number) {
        return angle - Math.PI / 2
      }

      if (!item) return
      if (!item.visible) item.visible = true

      item.matrix.makeRotationFromEuler(new Euler(0, getRightPerpendicular(-quatY), 0, 'XYZ'))

      const yPos = itemPosition.y

      const offsetX = (thickness / 2) * Math.cos(quatY)
      const offsetZ = (thickness / 2) * Math.sin(quatY)

      const finalXWithOffset = finalX + offsetX
      const finalZWithOffset = finalZ + offsetZ

      item.matrix.setPosition(finalXWithOffset, yPos, finalZWithOffset)
      item.x = finalXWithOffset
      item.y = finalY
      item.z = finalZWithOffset
      item.snappedWall = hiddenWallID
    })
  },
  addItemToHiddenWall: (hiddenWallID: AnyID, itemID: AnyID) => {
    set((store) => {
      const hiddenWall = store.items.get(hiddenWallID)
      if (!isHiddenWall(hiddenWall)) return

      const item = store.items.get(itemID)
      if (!item) return

      // Agregar a lowItems o highItems según el tipo
      if (item.tipoNormalizado === 'MAL') {
        hiddenWall.highItems.add(itemID)
      }
      if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        hiddenWall.lowItems.add(itemID)
      }
    })
  },
  removeItemFromHiddenWall: (hiddenWallID: AnyID, itemID: AnyID) => {
    set((store) => {
      const hiddenWall = store.items.get(hiddenWallID)
      if (!isHiddenWall(hiddenWall)) return

      const item = store.items.get(itemID)
      if (!item) return
      // Eliminar de lowItems o highItems según el tipo
      if (item.tipoNormalizado === 'MAL') {
        hiddenWall.highItems.delete(itemID)
      } else if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        hiddenWall.lowItems.delete(itemID)
      }
    })
  },
})
